/**
 * component-events-hub.js
 * - Populates filter dropdowns from event data attributes
 * - Filters event list on selection
 * - Updates featured card on row hover
 */

(function () {
  'use strict';

  const hub = document.querySelector('.events-hub');
  if (!hub) return;

  const listItems     = hub.querySelectorAll('.event-list-item');
  const filterBtns    = hub.querySelectorAll('.events-hub__filter-btn');
  const featuredWrap    = hub.querySelector('.events-hub__featured-wrap');
  const featuredImg     = hub.querySelector('#featured-event-img');
  const featuredName    = hub.querySelector('#featured-event-name');
  const featuredDate    = hub.querySelector('#featured-event-date');
  const featuredCity    = hub.querySelector('#featured-event-city');
  const featuredUrl     = hub.querySelector('#featured-event-url');
  const featuredImgLink = hub.querySelector('#featured-event-img-link');
  const featuredAddress = hub.querySelector('#featured-event-address');
  const featuredTime    = hub.querySelector('#featured-event-time');

  /* ── Read initial filters from URL ── */
  const urlParams     = new URLSearchParams(window.location.search);
  const activeFilters = {
    city:     urlParams.get('city')     || '',
    activity: urlParams.get('activity') || '',
    type:     urlParams.get('type')     || '',
    date:     urlParams.get('date')     || ''
  };

  /* ── Build filter dropdowns ── */
  /* Maps hold filter value → display label; values stay lowercase for matching */
  const optionSets = { city: new Map(), activity: new Map(), type: new Map() };

  const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);

  listItems.forEach(item => {
    const city     = item.dataset.city?.trim();
    const activity = item.dataset.activity?.trim();
    const tags     = item.dataset.type?.trim();
    /* data-city-label preserves the casing authored in the metaobject */
    if (city)     optionSets.city.set(city, item.dataset.cityLabel?.trim() || capitalize(city));
    if (activity) optionSets.activity.set(activity, capitalize(activity));
    if (tags) tags.split(',').forEach(t => { const v = t.trim(); if (v) optionSets.type.set(v, capitalize(v)); });
  });

  filterBtns.forEach(btn => {
    const key      = btn.dataset.filter;
    const dropdown = btn.nextElementSibling;
    if (!dropdown || !optionSets[key]) return;

    optionSets[key].forEach((label, val) => {
      const li = document.createElement('li');
      li.role = 'option';
      li.dataset.value = val;
      li.textContent = label;
      /* Restore selection from URL */
      if (activeFilters[key] === val) li.setAttribute('aria-selected', 'true');
      dropdown.appendChild(li);
    });

    /* Restore button active state from URL */
    if (activeFilters[key]) btn.classList.add('events-hub__filter-btn--active');
  });

  /* ── Toggle dropdowns ── */
  filterBtns.forEach(btn => {
    btn.addEventListener('click', e => {
      e.stopPropagation();
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      closeAllDropdowns();
      if (!expanded) openDropdown(btn);
    });
  });

  document.addEventListener('click', closeAllDropdowns);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeAllDropdowns(); });

  function openDropdown(btn) {
    btn.setAttribute('aria-expanded', 'true');
    btn.nextElementSibling?.removeAttribute('hidden');
  }

  function closeAllDropdowns() {
    filterBtns.forEach(b => {
      b.setAttribute('aria-expanded', 'false');
      b.nextElementSibling?.setAttribute('hidden', '');
    });
  }

  /* ── Handle filter option selection ── */
  hub.addEventListener('click', e => {
    const option = e.target.closest('.events-hub__filter-dropdown [role="option"]');
    if (!option) return;

    const dropdown = option.closest('.events-hub__filter-dropdown');
    const btn      = dropdown?.previousElementSibling;
    if (!btn) return;

    const key = btn.dataset.filter;
    const val = option.dataset.value;

    /* Toggle: clicking an active option deselects it */
    const isActive = option.getAttribute('aria-selected') === 'true' && val !== '';
    dropdown.querySelectorAll('[role="option"]').forEach(o => o.setAttribute('aria-selected', 'false'));

    if (isActive) {
      activeFilters[key] = '';
      btn.classList.remove('events-hub__filter-btn--active');
    } else {
      option.setAttribute('aria-selected', 'true');
      activeFilters[key] = val;
      btn.classList.toggle('events-hub__filter-btn--active', val !== '');
    }

    closeAllDropdowns();
    applyFilters();
    pushFiltersToURL();
  });

  /* ── Apply filters + no-results message ── */
  const emptyMsg = hub.querySelector('.events-hub__empty');

  function applyFilters() {
    const now   = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let visibleCount = 0;

    listItems.forEach(item => {
      let visible = true;

      if (activeFilters.city && item.dataset.city !== activeFilters.city) visible = false;
      if (activeFilters.activity && item.dataset.activity !== activeFilters.activity) visible = false;
      if (activeFilters.type && !item.dataset.type?.split(',').map(t => t.trim()).includes(activeFilters.type)) visible = false;

      if (activeFilters.date && item.dataset.date) {
        const itemDate = new Date(item.dataset.date);
        const thisWeekEnd   = new Date(today); thisWeekEnd.setDate(today.getDate() + 7);
        const thisMonthEnd  = new Date(today.getFullYear(), today.getMonth() + 1, 1);
        const nextMonthEnd  = new Date(today.getFullYear(), today.getMonth() + 2, 1);

        if (activeFilters.date === 'this_week'  && !(itemDate >= today && itemDate < thisWeekEnd))  visible = false;
        if (activeFilters.date === 'this_month' && !(itemDate >= today && itemDate < thisMonthEnd)) visible = false;
        if (activeFilters.date === 'next_month' && !(itemDate >= thisMonthEnd && itemDate < nextMonthEnd)) visible = false;
      }

      item.style.display = visible ? '' : 'none';
      if (visible) visibleCount++;
    });

    /* Show / hide featured column based on whether the active event is still visible */
    syncFeaturedVisibility();

    /* Show / hide no-results message */
    const list = hub.querySelector('.events-hub__list');
    let noResults = list?.querySelector('.events-hub__no-results');
    if (visibleCount === 0) {
      if (!noResults) {
        noResults = document.createElement('p');
        noResults.className = 'events-hub__no-results';
        noResults.textContent = 'No events match the selected filters.';
        list?.appendChild(noResults);
      }
      noResults.style.display = '';
    } else if (noResults) {
      noResults.style.display = 'none';
    }
  }

  /* ── Sync filters to URL (replaceState — no history entry) ── */
  function pushFiltersToURL() {
    const params = new URLSearchParams();
    Object.entries(activeFilters).forEach(([key, val]) => {
      if (val) params.set(key, val);
    });
    const qs = params.toString();
    history.replaceState(null, '', qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
  }

  /* ── Sync featured column visibility with the active (hovered) item ── */
  function syncFeaturedVisibility() {
    if (!featuredWrap) return;
    const hovered = hub.querySelector('.event-list-item--hovered');
    const hide = !!(hovered && hovered.style.display === 'none');
    hub.classList.toggle('events-hub--no-featured', hide);
  }

  /* ── Apply filters on load if URL had params (no animation on initial state) ── */
  const hasInitialFilters = Object.values(activeFilters).some(v => v !== '');
  hub.classList.add('events-hub--skip-transition');
  if (hasInitialFilters) applyFilters();

  const isDesktop = () => window.innerWidth > 989;

  /* ── Row interaction: select on desktop, navigate on mobile ── */
  listItems.forEach(item => {
    /* Desktop hover → preview in featured card */
    item.addEventListener('mouseenter', () => {
      if (isDesktop()) updateFeaturedCard(item);
    });

    /* Click behavior differs by breakpoint */
    item.addEventListener('click', e => {
      if (isDesktop()) {
        /* Desktop: select event, update featured card, no navigation */
        e.preventDefault();
        updateFeaturedCard(item);
      } else {
        /* Mobile: navigate to product page */
        const url = item.dataset.eventUrl || (item.getAttribute('href'));
        if (item.tagName !== 'A' && url && url !== '#') {
          e.preventDefault();
          window.location.href = url;
        }
        /* <a> tags navigate naturally */
      }
    });
  });

  /* ── Pre-select first item on desktop load ── */
  /*
  if (isDesktop() && listItems.length > 0) {
    updateFeaturedCard(listItems[0]);
    syncFeaturedVisibility();
  }
  */

  /* Re-enable transitions after initial state is committed */
  requestAnimationFrame(() => requestAnimationFrame(() => hub.classList.remove('events-hub--skip-transition')));

  function updateFeaturedCard(item) {
    if (!featuredName) return;

    const name     = item.dataset.eventName        || '';
    const dateDisp = item.dataset.eventDateDisplay || '';
    const city     = item.dataset.eventCity        || '';
    const address  = item.dataset.eventAddress     || '';
    const time     = item.dataset.eventTime        || '';
    const imgSrc   = item.dataset.eventImage       || '';
    const url      = item.dataset.eventUrl         || item.getAttribute('href') || '';

    featuredName.textContent = name;
    if (featuredDate) featuredDate.textContent = dateDisp;

    if (featuredCity) {
      featuredCity.textContent = city;
      featuredCity.style.display = city ? '' : 'none';
    }

    if (featuredAddress) {
      featuredAddress.textContent = address;
      featuredAddress.style.display = address ? '' : 'none';
    }

    if (featuredTime) {
      featuredTime.textContent = time;
      featuredTime.style.display = time ? '' : 'none';
    }

    if (imgSrc && featuredImg && featuredImg.tagName === 'IMG') {
      featuredImg.src = imgSrc;
      featuredImg.alt = name;
    }

    /* Update both clickable links in the featured card */
    if (featuredUrl)     featuredUrl.href     = url;
    if (featuredImgLink) featuredImgLink.href = url;

    listItems.forEach(i => i.classList.remove('event-list-item--hovered'));
    item.classList.add('event-list-item--hovered');
  }

  /* ============================================================
     MOBILE FILTER DRAWER
  ============================================================ */

  const filterToggle  = document.getElementById('events-filter-toggle');
  const filterDrawer  = document.getElementById('events-filter-drawer');
  const filterClose   = document.getElementById('events-filter-close');
  const filterApply   = document.getElementById('events-filter-apply');
  const filterClear   = document.getElementById('events-filter-clear');
  const drawerBody    = document.getElementById('events-filter-drawer-body');

  if (filterToggle && filterDrawer) {

    const drawerFilters = { city: '', activity: '', type: '', date: '' };

    /* Build drawer content from same optionSets used by desktop */
    const drawerConfig = [
      { key: 'city',     label: 'Location' },
      { key: 'activity', label: 'Activity' },
      { key: 'type',     label: 'Type'     },
      { key: 'date',     label: 'Date',
        staticOptions: [
          { value: 'this_week',  label: 'This week'   },
          { value: 'this_month', label: 'This month'  },
          { value: 'next_month', label: 'Next month'  }
        ]
      }
    ];

    drawerConfig.forEach(cfg => {
      const values = cfg.staticOptions
        ? cfg.staticOptions
        : Array.from(optionSets[cfg.key] || [], ([value, label]) => ({ value, label }));

      if (!values.length) return;

      const item = document.createElement('div');
      item.className = 'events-hub__drawer-item';

      const header = document.createElement('button');
      header.className = 'events-hub__drawer-item-header';
      header.type = 'button';
      header.innerHTML = `${cfg.label}
        <svg class="events-hub__drawer-chevron" width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true">
          <path d="M1 1l5 5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>`;

      const options = document.createElement('div');
      options.className = 'events-hub__drawer-options';

      values.forEach(opt => {
        const btn = document.createElement('button');
        btn.className = 'events-hub__drawer-option';
        btn.type = 'button';
        btn.dataset.key = cfg.key;
        btn.dataset.value = opt.value;
        btn.textContent = opt.label;
        btn.addEventListener('click', () => {
          const isActive = btn.classList.contains('is-active');
          options.querySelectorAll('.events-hub__drawer-option').forEach(b => b.classList.remove('is-active'));
          if (!isActive) {
            btn.classList.add('is-active');
            drawerFilters[cfg.key] = opt.value;
          } else {
            drawerFilters[cfg.key] = '';
          }
          updateDrawerClearBtn();
        });
        options.appendChild(btn);
      });

      header.addEventListener('click', () => item.classList.toggle('is-open'));
      item.appendChild(header);
      item.appendChild(options);
      drawerBody.appendChild(item);
    });

    function updateDrawerClearBtn() {
      const hasActive = Object.values(drawerFilters).some(v => v !== '');
      filterClear.classList.toggle('is-visible', hasActive);
    }

    /* Open drawer */
    filterToggle.addEventListener('click', () => {
      hub.classList.add('events-hub--filter-open');
      filterDrawer.setAttribute('aria-hidden', 'false');
      filterToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('events-filter-open');
    });

    /* Close drawer */
    function closeDrawer() {
      hub.classList.remove('events-hub--filter-open');
      filterDrawer.setAttribute('aria-hidden', 'true');
      filterToggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('events-filter-open');
    }

    filterClose.addEventListener('click', closeDrawer);

    /* Apply — copy drawer selections into activeFilters, run, and sync URL */
    filterApply.addEventListener('click', () => {
      Object.assign(activeFilters, drawerFilters);
      applyFilters();
      pushFiltersToURL();
      closeDrawer();
    });

    /* Clear all */
    filterClear.addEventListener('click', () => {
      Object.keys(drawerFilters).forEach(k => drawerFilters[k] = '');
      drawerBody.querySelectorAll('.events-hub__drawer-option').forEach(b => b.classList.remove('is-active'));
      updateDrawerClearBtn();
    });
  }

})();
